import { Component, OnInit } from '@angular/core';
import { ColDef, GridOptions, GridApi } from "ag-grid-community";
import { SubHeader } from "../../../core/models/sub-header.model";
import { SubHeaderService } from "../../../core/services/sub-header.service";
import { Subscription } from "rxjs";
import { GridFormatterService } from "../../../common/services/grid-formatter.service";
import { tap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { SystemAlert } from '../../../services/application-config.service';
import { Dialog, DialogButton } from '../../../core/models/dialog.model';
import { DynamicDialogService } from '../../../core/services/dynamic-dialog.service';
import { ButtonRendererComponent } from "../../../common/components/ag-grid/button-renderer.component";

@Component({
    selector: 'app-system-alerts',
    templateUrl: './system-alerts.component.html',
    styleUrls: ['./system-alerts.component.scss'],
    standalone: false
})
export class SystemAlertsComponent implements OnInit {

	gridOptions: GridOptions;
	gridApi?: GridApi;
    //we can get rid of these buttons and leave them in the grid?
    private subheader = new SubHeader({ title: "System Alerts", buttons: [{ name: "New System Alert", method: () => this.onCreate() }] });
    subscription: Subscription = new Subscription();
    public alerts: SystemAlert[] = [];
    public showEditModal: boolean = false;
    public showCreateModal: boolean = false;
    public selectedAlert: SystemAlert;
    public savingAlert: boolean = false;

    confirmAlertDeleteDialog: Dialog = new Dialog("Are you sure you want to delete this system alert?", [new DialogButton("Delete the alert", true, () => this.completeDelete(this.selectedAlert)), new DialogButton("Cancel", true, () => { this.handleCancel() })]);
    updateSuccessfulDialog: Dialog = new Dialog("Update Successful", [new DialogButton("OK", true, () => { this.handleCancel() })]);
    deleteSuccesfulDialog: Dialog = new Dialog("Delete Successful", [new DialogButton("OK", true, () => { this.handleCancel() })]);

    constructor(private subheaderSvc: SubHeaderService, private formatter: GridFormatterService, public http: HttpClient, public dialogService: DynamicDialogService) {
    }

    ngOnInit() {
        this.gridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.createColumnDefinitions(), false, true, true, false);
        this.subheaderSvc.updateSubHeader(this.subheader);
        this.gridOptions.overlayLoadingTemplate = '<span class="ag-overlay-loading-center">Please wait ...</span>';
        this.gridOptions.suppressColumnVirtualisation = true;
        this.gridOptions.pagination = false;
      

        this.gridOptions.onGridReady = (params) => {
			this.gridApi = params.api;
			this.gridApi.sizeColumnsToFit();
        };

        this.getSystemAlerts();
    }


    ngOnDestroy() {
        if (this.subscription) this.subscription.unsubscribe();
    }


    createColumnDefinitions(): ColDef[] {
      return [
          {
              headerName: "Title",
              headerTooltip: "Title",
              field: "title",
              tooltipField: "title",
              flex: 1,
              maxWidth: 250,
              minWidth: 100
          } as ColDef,
          {
              headerName: "Message",
              headerTooltip: "Message",
              field: "message",
              tooltipField: "message",
              filter: "agTextColumnFilter",
              filterParams: this.formatter.defaultTextFilterParams,
              flex: 4,
              minWidth: 100
          } as ColDef,
          {
              headerName: "Start Date",
              headerTooltip: "Start Date",
              field: "startDate",
              filter: "agDateColumnFilter",
              filterParams: this.formatter.defaultDateFilterParams,
              valueFormatter: this.formatter.formatDate("startDate", "MM/dd/yyyy hh:mm:ss a"),
              tooltipValueGetter: this.formatter.formatDate("startDate", "MM/dd/yyyy hh:mm:ss a"),
              flex: 1,
              minWidth: 125,
              maxWidth: 150
          } as ColDef,
          {
              headerName: "End Date",
              headerTooltip: "End Date",
              field: "endDate",
              filter: "agDateColumnFilter",
              filterParams: this.formatter.defaultDateFilterParams,
              valueFormatter: this.formatter.formatDate("endDate", "MM/dd/yyyy hh:mm:ss a"),
              tooltipValueGetter: this.formatter.formatDate("endDate", "MM/dd/yyyy hh:mm:ss a"),
              flex: 1,
              minWidth: 125,
              maxWidth: 150
          } as ColDef,
          {
              headerName: "Edit",
              headerTooltip: "Edit",
              field: "actions",
              cellRenderer: ButtonRendererComponent,
              cellRendererParams: {
                    buttonText: "Edit",
                    action: (obj: any) => this.onEdit(obj)
              },             
              flex: 1,
              minWidth: 125,
              maxWidth: 150,
              filter: false,
              sortable: false
          } as ColDef,
          {
              headerName: "Delete",
              headerTooltip: "Delete",
              field: "Delete",
              cellRenderer: ButtonRendererComponent,
              cellRendererParams: {
                    buttonText: "Delete",
                    action: (obj: any) => this.onDelete(obj)
              },  
              flex: 2,
              minWidth: 125,
              maxWidth: 150,
              filter: false,
              sortable: false
          } as ColDef
      ] as ColDef[];
  }

   public getSystemAlerts() {
       let obs$ = this.http.get<SystemAlert[]>("/api/systemNotifications/allAlerts").pipe(
           tap((alerts: SystemAlert[]) => {
            this.alerts = alerts.sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()); 
        })
      )

        let subs$ = obs$.subscribe(alerts => {
            this.alerts = alerts;
            if (this.gridApi) {
                this.gridApi.setGridOption("rowData", this.alerts);
				this.gridApi.setGridOption("loading", false);
                this.gridApi.sizeColumnsToFit();
            }
        });

      this.subscription.add(subs$);
    }

    public onEdit(alert: SystemAlert) {
        this.showEditModal = true;
        this.selectedAlert = alert; 
    }

    public onCreate() {
        this.showCreateModal = true;
    }

    public onDelete(alert: SystemAlert) {
        this.confirmAlertDeleteDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.confirmAlertDeleteDialog);
        this.selectedAlert = alert;
    }

    public completeDelete(alert: SystemAlert) {
        this.savingAlert = true;
        this.http.delete(`/api/systemNotifications/deleteAlert/${alert.id}`).subscribe({
            next: (response) => {
                this.savingAlert = false; 
                this.getSystemAlerts();
                this.deleteSuccesfulDialog.toggleDialog(true);
                this.dialogService.updateDialog(this.deleteSuccesfulDialog);
            },
            error: (error) => {
                console.error('Error deleting alert', error);
            }
        });

    }

    public completeUpdate(updatedAlert: SystemAlert) {
        this.savingAlert = true;
        //Update the alert in the database
        var alert = this.convertTimesToUTC(updatedAlert);

        this.http.put(`/api/systemNotifications/updateAlert/`, alert).subscribe({
           next: (response) => {
                this.savingAlert = false;
                this.getSystemAlerts();
                this.updateSuccessfulDialog.toggleDialog(true);
                this.dialogService.updateDialog(this.updateSuccessfulDialog);
            },
            error: (error) => {
                console.error('Error updating alert', error);
            }

        });
        
    }

    public completeCreateAlert(newAlert: SystemAlert) {
        this.savingAlert = true;

        var alert = this.convertTimesToUTC(newAlert);

        this.http.post(`/api/systemNotifications/createAlert/`, alert).subscribe({
            next: (response) => {
                this.savingAlert = false; 
                this.showCreateModal = false;
                this.getSystemAlerts();

            },
            error: (error) => {
                console.error('Error creating alert', error);
            }
        });

    }

    public convertTimesToUTC(alert: SystemAlert): SystemAlert {
        //convert to UTC for save to database
        var startDate = new Date(alert.startDate);
        var endDate = new Date(alert.endDate);
        alert.startDate = new Date(startDate.toUTCString());
        alert.endDate = new Date(endDate.toUTCString());
        return alert;

    }

    
    public handleCancel() {
        this.showEditModal = false; 
        this.showCreateModal = false;
    }

}
